<template>
    <div
        id="sports"
        v-bind:class="{
            _bsi: !isAsi,
            ID_ID: i18n.locale.value === LanguageType.ID_ID,
            TH_TH: i18n.locale.value === LanguageType.TH_TH,
            MY_MM: i18n.locale.value === LanguageType.MY_MM,
            VI_VN: i18n.locale.value === LanguageType.VI_VN,
            KO_KR: i18n.locale.value === LanguageType.KO_KR,
            TA_IN: i18n.locale.value === LanguageType.TA_IN,
            KM_KH: i18n.locale.value === LanguageType.KM_KH,
            _euroSiteStyle: isEuroSite,
            _isFromApp: isFromApp,
        }"
        class="container sports"
        style="overflow: unset;"
    >
        <div v-if="!isFromApp" id="pp-placeholder" />
        <RouterView />

        <BetSlip v-if="isAsi && isShowBetSlip" />

        <BottomFunction v-if="isAsi && !isExchangePage && !isFantasySelectionPage" />

        <ProductPlatform v-if="isHeaderEnabled" />

        <SystemIcons />
        <SportIcons />
        <Transition name="fade-out">
            <Message
                v-if="topScreenToast.messageKey"
                v-bind:status="topScreenToast.status"
                v-bind:messageKey="topScreenToast.messageKey"
                v-bind:messageData="topScreenToast.messageData"
                v-bind:class="[topScreenToast.messagePosition || 'position-top']"
            />
        </Transition>

        <Transition name="fade">
            <BackToTop v-if="isShowBackToTop" />
        </Transition>

        <PopupTelegramBot
            v-if="activePopup === MainPagePopup.TelegramBot"
            v-on:close="closePopup(MainPagePopup.TelegramBot)"
        />

        <JackpotWinnerPopup
            v-else-if="activePopup === MainPagePopup.JackpotWinner"
            v-on:close="closePopup(MainPagePopup.JackpotWinner)"
        />

        <PopupBetBuilderTutorial
            v-else-if="activePopup === MainPagePopup.BetBuilderTutorial"
            v-on:close="closePopup(MainPagePopup.BetBuilderTutorial)"
        />

        <PopupVoucherRedeem
            v-else-if="activePopup === MainPagePopup.VoucherRedeem"
            v-on:close="closePopup(MainPagePopup.VoucherRedeem)"
        />

        <BetSlipTutorial
            v-if="betSlipTutorial"
            v-bind:showCloseButton="betSlipTutorial.showCloseButton"
            v-on:close="betSlipTutorial.close"
        />

        <PopupAcceptAnyOdds v-if="isAsi && showAcceptAnyOddsBanner" />
        <QuickDeposit />

        <template v-if="isAsi && !isFromApp">
            <GamesWidget v-if="isGamesWidgetEnabled" />
            <CasinoWidget v-if="isCasinoWidgetEnabled" />
        </template>
    </div>
</template>

<script setup lang="ts">
    import Cookies from 'js-cookie';
    import throttle from 'lodash/throttle';
    import { storeToRefs } from 'pinia';
    import { computed, onBeforeUnmount, onMounted, onUnmounted, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import JackpotWinnerPopup from './components/jackpot/JackpotWinnerPopup.vue';
    import { useApp } from './libraries/composables/useApp';
    import { useFlutterChannel } from './libraries/composables/useFlutterChannel';
    import { useOutrightBetSlipStore } from './stores/betSlip/outrightBetSlipStore';
    import { useSingleBetSlipStore } from './stores/betSlip/singleBetSlipStore';
    import { useFeatureToggleStore } from './stores/featureToggleStore';
    import { useRootStore } from './stores/rootStore';
    import BetSlipTutorial from '@/components/BetSlipTutorial.vue';
    import BottomFunction from '@/components/BottomFunction.vue';
    import PopupAcceptAnyOdds from '@/components/PopupAcceptAnyOdds.vue';
    import PopupBetBuilderTutorial from '@/components/PopupBetBuilderTutorial.vue';
    import PopupTelegramBot from '@/components/PopupTelegramBot.vue';
    import PopupVoucherRedeem from '@/components/PopupVoucherRedeem.vue';
    import ProductPlatform from '@/components/ProductPlatform.vue';
    import QuickDeposit from '@/components/QuickDeposit.vue';
    import BetSlip from '@/components/betSlip/BetSlip.vue';
    import BackToTop from '@/components/common/BackToTop.vue';
    import Message from '@/components/common/Message.vue';
    import CasinoWidget from '@/components/widget/CasinoWidget.vue';
    import GamesWidget from '@/components/widget/GamesWidget.vue';
    import type { IMixParlayBetSlip } from '@/interface/betSlip';
    import { LanguageType, MainPagePopup, RoutePage } from '@/interface/enum';
    import { resolveBetSlipRestoreView } from '@/libraries/betSlip/betSlipRestorePolicy';
    import { BET_SLIP_STORAGE_KEYS, betSlipStorageKeysToClear } from '@/libraries/betSlip/betSlipStorageKeys';
    import { usePopupPriority } from '@/libraries/composables/popup/usePopupPriority';
    import { useSingleModeBetSlipTutorialState } from '@/libraries/composables/popup/useSingleModeBetSlipTutorialState';
    import useScroll from '@/libraries/composables/useScroll';
    import type { IEuroTicket } from '@/libraries/euroTicket';
    import { withResolvedOddsOption, withResolvedOddsOptions } from '@/libraries/euroTicket';
    import { applyEuroGtm, applyNormalGtm, setPlatformDimension } from '@/libraries/helpers/gtmHelper';
    import { i18n } from '@/libraries/i18n';
    import { getHost } from '@/libraries/url';
    import { getStorageItem, setStorageItem } from '@/libraries/utils/storage';
    import { useBetBuilderBetSlipStore } from '@/stores/betSlip/betBuilderBetSlipStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useDynamicMarketBetSlipStore } from '@/stores/betSlip/dynamicMarketBetSlipStore';
    import { useParlayBetSlipStore } from '@/stores/betSlip/parlayBetSlipStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';
    import SportIcons from '@/svg/SportIcons.vue';
    import SystemIcons from '@/svg/SystemIcons.vue';

    const {
        isShowBackToTop,
        handleScrollEvent: _handleScrollEvent,
        handleScrollBackToTop: _handleScrollBackToTop,
        handleOpenTicketScrollOdds,
    } = useScroll();

    const handleScrollEvent = throttle(_handleScrollEvent, 200);
    window.addEventListener('scroll', handleScrollEvent);
    const handleScrollBackToTop = throttle(_handleScrollBackToTop, 200);
    window.addEventListener('scroll', handleScrollBackToTop);

    const { isShowBetSlip, lastBetSlipType, lastBetSlipState, betSlipType, betSlipState } = storeToRefs(useBetSlipStore());

    const {
        isEuroSite,
        isGamesWidgetEnabled,
        isCasinoWidgetEnabled,
        isSingleModeBetSlipEnabled,
    } = storeToRefs(useFeatureToggleStore());
    const { isAsi, isMixParlayMode, topScreenToast, showAcceptAnyOddsBanner, oddsPosition } = storeToRefs(useRootStore());

    const route = useRoute();
    const routerPages = [RoutePage.Exchange, RoutePage.ExchangeTransfer].map(page => page.toString());
    const isExchangePage = computed(() => route.name && routerPages.includes(route.name.toString()));

    const isFantasySelectionPage = computed(() => route.path.startsWith('/fantasy-selection'));

    const { isFromApp } = useApp();
    if (isFromApp) {
        useFlutterChannel();
    }

    const isHeaderEnabled = computed(() => !__MOCK_HEADER_ENABLED__ && !isFromApp);

    const { setOddsPosition, setShowAcceptAnyOddsBanner } = useRootStore();
    const { setBetSlipState, setBetSlipType, setLastBetSlipType, setLastBetSlipState, closeBetSlip } = useBetSlipStore();
    const { setBetSlip } = useSingleBetSlipStore();
    const { setBetSlip: setOutrightBetSlip } = useOutrightBetSlipStore();
    const { setBetSlip: setBetBuilderBetSlip } = useBetBuilderBetSlipStore();
    const { setBetSlip: setParlayBetSlip } = useParlayBetSlipStore();
    const { setBetSlip: setDynamicBetSlip } = useDynamicMarketBetSlipStore();
    const { restoreTickets: restoreSingleModeTickets } = useSingleModeBetSlipStore();

    function handleReloadBetSlip() {
        window.addEventListener('beforeunload', setLocalStorage);
        window.addEventListener('pagehide', setLocalStorage);

        const legacyBetSlipDataConfigs = [
            {
                key: BET_SLIP_STORAGE_KEYS.single,
                setter: (data: IEuroTicket) => setBetSlip(withResolvedOddsOption(data)),
            },
            { key: BET_SLIP_STORAGE_KEYS.outright, setter: setOutrightBetSlip },
            { key: BET_SLIP_STORAGE_KEYS.dynamicMarket, setter: setDynamicBetSlip },
        ];
        const betSlipDataConfigs = [
            { key: BET_SLIP_STORAGE_KEYS.betBuilder, setter: setBetBuilderBetSlip },
            {
                key: BET_SLIP_STORAGE_KEYS.mixParlay,
                setter: (data: IMixParlayBetSlip) => setParlayBetSlip({
                    ...data,
                    tickets: withResolvedOddsOptions(data.tickets),
                }),
            },
            ...(isSingleModeBetSlipEnabled.value
                ? [{ key: BET_SLIP_STORAGE_KEYS.singleMode, setter: restoreSingleModeTickets }]
                : legacyBetSlipDataConfigs),
        ];

        try {
            const expiration = getStorageItem<number>(BET_SLIP_STORAGE_KEYS.expiration);
            if (expiration === null || new Date().getTime() > expiration) {
                return;
            }

            const betSlipView = resolveBetSlipRestoreView(
                isSingleModeBetSlipEnabled.value,
                window.localStorage.getItem(BET_SLIP_STORAGE_KEYS.betSlipType),
                window.localStorage.getItem(BET_SLIP_STORAGE_KEYS.betSlipState),
            );

            const lastBetSlipView = resolveBetSlipRestoreView(
                isSingleModeBetSlipEnabled.value,
                window.localStorage.getItem(BET_SLIP_STORAGE_KEYS.lastBetSlipType),
                window.localStorage.getItem(BET_SLIP_STORAGE_KEYS.lastBetSlipState),
            );

            betSlipDataConfigs.forEach(({ key, setter }) => {
                const data = getStorageItem(key);
                if (data) {
                    setter(data);
                }
            });

            setBetSlipType(betSlipView.betSlipType);
            setBetSlipState(betSlipView.betSlipState);
            setLastBetSlipType(lastBetSlipView.betSlipType);
            setLastBetSlipState(lastBetSlipView.betSlipState);
        } catch (error) {
            console.error('Error while reloading bet slip', error);
        } finally {
            betSlipStorageKeysToClear.forEach(key => window.localStorage.removeItem(key));
        }
    }

    function setLocalStorage() {
        setStorageItem(BET_SLIP_STORAGE_KEYS.expiration, new Date().getTime() + 15 * 1000);

        const { betSlip, hasTicket: hasSingleTicket } = storeToRefs(useSingleBetSlipStore());
        if (hasSingleTicket.value) {
            setStorageItem(BET_SLIP_STORAGE_KEYS.single, betSlip.value);
        }

        const { betSlip: outrightBetSlip, hasTicket: hasOutrightTicket } = storeToRefs(useOutrightBetSlipStore());
        if (hasOutrightTicket.value) {
            setStorageItem(BET_SLIP_STORAGE_KEYS.outright, outrightBetSlip.value);
        }

        const { betSlip: dynamicBetSlip, hasTicket: hasDynamicTicket } = storeToRefs(useDynamicMarketBetSlipStore());
        if (hasDynamicTicket.value) {
            setStorageItem(BET_SLIP_STORAGE_KEYS.dynamicMarket, dynamicBetSlip.value);
        }

        const { betSlip: mixParlayBetSlip, ticketCount: parlayTicketCount } = storeToRefs(useParlayBetSlipStore());

        if (parlayTicketCount.value > 0) {
            setStorageItem(BET_SLIP_STORAGE_KEYS.mixParlay, mixParlayBetSlip.value);
        }

        const { betSlip: betBuilderBetSlip, ticketCount: betBuilderTicketCount } = storeToRefs(useBetBuilderBetSlipStore());

        if (betBuilderTicketCount.value > 0) {
            setStorageItem(BET_SLIP_STORAGE_KEYS.betBuilder, betBuilderBetSlip.value);
        }

        const { hasTickets } = storeToRefs(useSingleModeBetSlipStore());
        const { createSnapshot } = useSingleModeBetSlipStore();
        if (hasTickets.value) {
            setStorageItem(BET_SLIP_STORAGE_KEYS.singleMode, createSnapshot());
        }

        window.localStorage.setItem(BET_SLIP_STORAGE_KEYS.lastBetSlipType, lastBetSlipType.value);
        window.localStorage.setItem(BET_SLIP_STORAGE_KEYS.lastBetSlipState, lastBetSlipState.value);
        window.localStorage.setItem(BET_SLIP_STORAGE_KEYS.betSlipType, betSlipType.value);
        window.localStorage.setItem(BET_SLIP_STORAGE_KEYS.betSlipState, betSlipState.value);
    }

    function handleAaoBanner() {
        if (showAcceptAnyOddsBanner.value === null) {
            const cookiesAaoBannerShowed = Cookies.get('SportsAaoPopup');
            if (cookiesAaoBannerShowed) {
                setShowAcceptAnyOddsBanner(false);
            }
        }
    }

    const router = useRouter();
    function handleToRoute() {
        const route = Cookies.get('toSportsRoute');
        if (route) {
            Cookies.remove('toSportsRoute', { domain: getHost() });
            router.push(JSON.parse(route));
        }
    }

    // fix go to profile then click back button. mobile won't reload bet slip
    function backPageCallback() {
        const isByHistoryBackForward = window.performance?.navigation?.type === 2;
        if (isByHistoryBackForward) {
            handleReloadBetSlip();
        }
    }

    if (isAsi.value) {
        handleReloadBetSlip();
        handleAaoBanner();
    }
    handleToRoute();

    if (!__ENV_DEV__) {
        setPlatformDimension(isFromApp ? 'app' : 'web');
        // Append GTM script depend on isEuroSite
        if (isEuroSite.value) {
            applyEuroGtm();
        } else {
            applyNormalGtm();
        }
    }

    window.eventEmitter.on('pp.showSideBar', closeBetSlip);

    onMounted(() => {
        window.addEventListener('popstate', backPageCallback);
    });

    onUnmounted(() => {
        window.removeEventListener('popstate', backPageCallback);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', handleScrollEvent);
        window.removeEventListener('scroll', handleScrollBackToTop);
        window.eventEmitter.off('pp.showSideBar', closeBetSlip);
    });

    watch(oddsPosition, () => {
        if (oddsPosition.value !== 0 && !isMixParlayMode.value) {
            handleOpenTicketScrollOdds(oddsPosition.value);
            setOddsPosition(0);
        }
    });

    const { activePopup, closePopup } = usePopupPriority();

    const { isTutorialOpenFromBetSlip, closeTutorialFromBetSlip } = useSingleModeBetSlipTutorialState();

    const betSlipTutorial = computed(() => {
        if (activePopup.value === MainPagePopup.SingleModeBetSlipTutorial) {
            return {
                showCloseButton: false,
                close: () => closePopup(MainPagePopup.SingleModeBetSlipTutorial),
            };
        }

        if (isTutorialOpenFromBetSlip.value) {
            return { showCloseButton: true, close: closeTutorialFromBetSlip };
        }

        return null;
    });
</script>

<style lang="scss">
    @import "./scss/sports";

    .container {
        padding-top: 0 !important;

        &._isFromApp {
            padding-bottom: 120px;

            &:lang(my) {
                padding-bottom: 140px;
            }
        }
    }

    #pp-placeholder {
        height: 4.6rem;
        background-image: linear-gradient(180deg, #2699fb, #194d8c 56%, #093879);

        ._euroSiteStyle & {
            background-color: #0d47a1;
            background-image: inherit;
        }
    }
</style>
